import React, { useRef } from 'react';
import { cx } from '../../utils/cx.js';
import { FOCUS_RING } from './Button';

/**
 * Segmented control [ A | B | C ] — Insights purple accent. Behaves as a tab list:
 * ArrowLeft/ArrowRight move the selection, Home/End jump to the ends.
 * options: [{ value, label, icon?, count?, title? }]
 */
const SegmentedControl = ({
  value,
  onChange,
  options = [],
  size = 'md',
  fullWidth = false,
  ariaLabel,
  className = '',
}) => {
  const refs = useRef([]);
  const pad = size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3.5 py-1.5 text-sm';

  const select = (index) => {
    const option = options[index];
    if (!option) return;
    onChange?.(option.value);
    refs.current[index]?.focus();
  };

  const onKeyDown = (e, index) => {
    const last = options.length - 1;
    const moves = { ArrowRight: index === last ? 0 : index + 1, ArrowLeft: index === 0 ? last : index - 1, Home: 0, End: last };
    if (!(e.key in moves)) return;
    e.preventDefault();
    select(moves[e.key]);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cx(
        'inline-flex max-w-full gap-0.5 overflow-x-auto rounded-lg border border-gray-200 bg-gray-100 p-1 scrollbar-none dark:border-gray-800 dark:bg-night-900',
        fullWidth && 'flex w-full',
        className,
      )}
    >
      {options.map((opt, index) => {
        const active = opt.value === value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            ref={(el) => { refs.current[index] = el; }}
            type="button"
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            title={opt.title}
            onClick={() => onChange?.(opt.value)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cx(
              'inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-all duration-150 cursor-pointer',
              FOCUS_RING,
              pad,
              fullWidth && 'flex-1',
              active
                ? 'bg-white text-brand-600 shadow-sm dark:bg-night-750 dark:text-brand-400'
                : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100',
            )}
          >
            {Icon && <Icon className="h-4 w-4 flex-shrink-0" aria-hidden />}
            {opt.label}
            {opt.count !== undefined && opt.count !== null && (
              <span className={cx('rounded-full px-1.5 text-[11px] font-semibold tabular-nums', active ? 'bg-brand-50 text-brand-600 dark:bg-brand-900 dark:text-brand-300' : 'bg-gray-200/70 text-gray-500 dark:bg-white/5 dark:text-gray-400')}>
                {typeof opt.count === 'number' ? opt.count.toLocaleString('fr-FR') : opt.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
